import axiosInstance from "./axios";
import { errorToast } from "../shared/Toast";

// Feature-scoped API module for curbside / drive-thru. Same convention as pickupBlockApi.js:
// the shared axios instance (Bearer auth + 401 handling), reads return data or a safe
// default and toast on error, mutations return the record (or null) and let the caller
// toast success.

const EMPTY_CONFIGS = {
  branchId: null,
  branchName: null,
  timezone: null,
  branchCoordinates: { lat: null, lng: null },
  modes: {
    curbside: { mode: "curbside", offered: false },
    drivethru: { mode: "drivethru", offered: false },
  },
};

const EMPTY_ARRIVALS = { arrivals: [], count: 0, waiting: 0, unreachable: 0 };

// GET /api/admin/fulfillment-configs?branchId=
// -> { branchId, branchName, timezone, branchCoordinates, modes: { curbside, drivethru } }
// Each mode carries both the stored settings and a `live` evaluation, so the page can show
// what a customer would see right now rather than only what is configured.
export const getFulfillmentConfigs = async (branchId) => {
  try {
    const res = await axiosInstance.get("/api/admin/fulfillment-configs", { params: { branchId } });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error loading fulfillment settings");
    return EMPTY_CONFIGS;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? "Error loading fulfillment settings");
    return EMPTY_CONFIGS;
  }
};

// PUT /api/admin/fulfillment-configs { branchId, mode, ...fields }
// Creating the row is what makes the branch OFFER the mode — there is no separate enabled
// flag, so the first save on a mode turns it on.
export const saveFulfillmentConfig = async (payload) => {
  try {
    const res = await axiosInstance.put("/api/admin/fulfillment-configs", payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error saving fulfillment settings");
    return null;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? "Error saving fulfillment settings");
    return null;
  }
};

// PUT /api/admin/fulfillment-configs/bulk
//   { mode, allBranches | branchIds, overwrite, ...fields }
// Rolls a mode out across the estate. `overwrite: false` (the default) leaves branches that
// already offer the mode untouched and names them in `skippedBranches`, so a rollout can
// never silently flatten settings someone tuned by hand.
export const bulkSaveFulfillmentConfigs = async (payload) => {
  try {
    const res = await axiosInstance.put("/api/admin/fulfillment-configs/bulk", payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error applying settings across branches");
    return null;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? "Error applying settings across branches");
    return null;
  }
};

// PUT /api/admin/fulfillment-configs/pause { branchId, mode, isPaused }
// Its own call, not part of the form save: this is the switch someone hits during a rush and
// it must never carry a half-edited form with it.
export const setFulfillmentPaused = async (payload) => {
  try {
    const res = await axiosInstance.put("/api/admin/fulfillment-configs/pause", payload);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error updating pause state");
    return null;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? "Error updating pause state");
    return null;
  }
};

// DELETE /api/admin/fulfillment-configs { branchId, mode }
// The server returns 409 while customers are still waiting in that mode, so the message is
// surfaced rather than the failure being treated as generic.
export const disableFulfillmentMode = async (payload) => {
  try {
    const res = await axiosInstance.delete("/api/admin/fulfillment-configs", { data: payload });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error disabling mode");
    return null;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? "Error disabling mode");
    return null;
  }
};

// GET /api/admin/arrivals?branchId=&includeClosed=
// -> { arrivals: [...], count, waiting, unreachable }
export const getArrivals = async (params = {}) => {
  try {
    const res = await axiosInstance.get("/api/admin/arrivals", { params });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error loading arrivals");
    return EMPTY_ARRIVALS;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? "Error loading arrivals");
    return EMPTY_ARRIVALS;
  }
};

// GET /api/admin/arrivals/:orderId/events
// The append-only history, including REFUSED taps (cooldown, out of radius, unreachable
// branch) — the evidence that settles "I tapped three times and nobody came".
export const getArrivalEvents = async (orderId) => {
  try {
    const res = await axiosInstance.get(`/api/admin/arrivals/${orderId}/events`);
    if (res?.data && !res.data.isError) return res.data.data?.events ?? [];
    errorToast(res?.data?.message ?? "Error loading arrival history");
    return [];
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? "Error loading arrival history");
    return [];
  }
};

// The three staff transitions, identical to what the KDS performs — the server runs the same
// code path for both, only the recorded actor differs (source: "admin").
//
// Returns true on success so the caller can refresh; the server's own message is surfaced on
// failure because it is the useful one ("already closed", "no access to this branch").
const arrivalAction = (action, label) => async (orderId) => {
  try {
    const res = await axiosInstance.post(`/api/admin/arrivals/${orderId}/${action}`);
    if (res?.data && !res.data.isError) return true;
    errorToast(res?.data?.message ?? `Could not ${label}`);
    return false;
  } catch (error) {
    errorToast(error?.response?.data?.message ?? error?.message ?? `Could not ${label}`);
    return false;
  }
};

// Acknowledge tells the customer someone is coming out — it is the only one that pushes.
export const acknowledgeArrival = arrivalAction("acknowledge", "acknowledge this arrival");
export const handOverArrival = arrivalAction("handed-over", "mark this as handed over");
// "Nobody is here" — keeps the car in the queue but flags it.
export const disputeArrival = arrivalAction("dispute", "flag this arrival");
